// JoshSkills page (T-351, owner request 2026-11-03): the shadow trades the third-party day-trader-skill pack
// ("JoshSkills") would have made on our data, with an equity curve, a per-trade candle chart and the trade table,
// plus the skill catalogue (the former Skills page). READ-ONLY: data from joshskills/api.py (127.0.0.1:10010) via the
// '/api/joshskills' proxy; nothing here places an order. Native results use the pack's own fills (no costs); the
// "could have filled at" line is owner decision D3. Replay = past sessions, never counted toward the 4-week gate (D2).
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { createChart, CrosshairMode, LineStyle } from 'lightweight-charts'
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from 'recharts'
import { Panel, MONO } from '../components/deskParts'
import SkillLab from './SkillLab'
import { jsGet, money, rText, toCandles, tradeMarkers, statsLine, EXIT_TONE, VERDICT_TONE } from '../lib/joshskills'

const btn = { ...MONO, fontSize: '0.64rem', letterSpacing: '0.06em', padding: '3px 8px', borderRadius: 4, border: '1px solid #334155', color: '#cbd5e1', background: 'transparent', cursor: 'pointer' }
const on = { borderColor: '#38bdf8', color: '#38bdf8' }
const th = { textAlign: 'left', color: '#64748b', fontWeight: 400, padding: '2px 8px 2px 0', whiteSpace: 'nowrap' }
const td = { padding: '2px 8px 2px 0', color: '#cbd5e1', whiteSpace: 'nowrap' }
const verdictOf = (v) => { const s = (v || '').toLowerCase(); return s.includes('toward the target') ? 'toward target' : s.includes('toward the stop') ? 'toward stop' : (s.includes('coin flip') || s.includes('break-even')) ? 'coin flip' : 'unknown' }

// One trade's day: 5-minute candles, entry / stop / target / exit lines and markers (New York wall time).
function TradeChart({ trade }) {
  const box = useRef(null)
  const [err, setErr] = useState(null)
  useEffect(() => {
    if (!trade || !box.current) return undefined
    const chart = createChart(box.current, {
      height: 320, layout: { background: { color: '#0f172a' }, textColor: '#64748b' },
      grid: { vertLines: { color: '#1e293b' }, horzLines: { color: '#1e293b' } }, crosshair: { mode: CrosshairMode.Normal },
      rightPriceScale: { borderColor: '#1e293b' }, timeScale: { borderColor: '#1e293b', timeVisible: true, secondsVisible: false },
    })
    const series = chart.addCandlestickSeries({ upColor: '#34d399', downColor: '#f87171', borderVisible: false, wickUpColor: '#34d399', wickDownColor: '#f87171' })
    let alive = true
    jsGet(`/bars?symbol=${encodeURIComponent(trade.symbol)}&date=${String(trade.trade_date).slice(0, 10)}`).then(d => {
      if (!alive) return
      series.setData(toCandles(d.bars))
      const line = (price, color, title, style = LineStyle.Dashed) => price != null && series.createPriceLine({ price: Number(price), color, lineWidth: 1, lineStyle: style, axisLabelVisible: true, title })
      line(trade.entry_price, '#38bdf8', 'entry', LineStyle.Solid)
      line(trade.stop, '#f87171', 'stop')
      line(trade.target, '#34d399', 'target')
      if (trade.exit_price != null) line(trade.exit_price, EXIT_TONE[trade.exit_reason] || '#94a3b8', `exit ${trade.exit_reason}`, LineStyle.Dotted)
      series.setMarkers(tradeMarkers(trade))
      chart.timeScale().fitContent()
    }).catch(e => alive && setErr(e.message))
    return () => { alive = false; chart.remove() }
  }, [trade])
  if (!trade) return <div style={{ color: '#64748b', fontSize: '0.75rem' }}>Pick a trade in the table to see its chart.</div>
  return (
    <div>
      <div style={{ ...MONO, fontSize: '0.72rem', color: '#cbd5e1', marginBottom: 4 }}>
        {trade.symbol} {String(trade.trade_date).slice(0, 10)} · buy {Number(trade.qty)} @ {Number(trade.entry_price).toFixed(2)} · stop {Number(trade.stop).toFixed(2)} · target {Number(trade.target).toFixed(2)} ({trade.target_r}R) ·
        {' '}<span style={{ color: EXIT_TONE[trade.exit_reason] || '#94a3b8' }}>{trade.status === 'open' ? 'OPEN' : `${trade.exit_reason} @ ${Number(trade.exit_price).toFixed(2)} ${rText(trade.r_multiple)} ${money(trade.pnl)}`}</span>
      </div>
      <div style={{ fontSize: '0.7rem', color: VERDICT_TONE[verdictOf(trade.tm_verdict)], marginBottom: 4 }}>Time Machine at entry: {trade.tm_verdict || '—'}</div>
      {err && <div style={{ color: '#f87171', fontSize: '0.72rem' }}>chart data error: {err}</div>}
      <div ref={box} />
    </div>
  )
}

function ShadowView() {
  const [mode, setMode] = useState('replay')
  const [data, setData] = useState(null)
  const [err, setErr] = useState(null)
  const [pick, setPick] = useState(null)
  useEffect(() => {
    let alive = true
    setData(null); setErr(null); setPick(null)
    const load = () => jsGet(`/overview?mode=${mode}`).then(d => { if (alive) { setData(d); setErr(null) } }).catch(e => alive && setErr(e.message))
    load()
    const id = setInterval(load, 60000)
    return () => { alive = false; clearInterval(id) }
  }, [mode])
  const trades = useMemo(() => [...(data?.trades || [])].reverse(), [data])
  const s = data?.stats
  const failed = data?.failed_24h || []
  return (
    <div className="space-y-3">
      <Panel title="JoshSkills shadow trades" right={<span className="flex gap-2">
        {['replay', 'live'].map(m => <button key={m} style={{ ...btn, ...(mode === m ? on : {}) }} onClick={() => setMode(m)}>{m.toUpperCase()}</button>)}
      </span>}>
        {err && <div style={{ color: '#f87171', fontSize: '0.75rem' }}>JoshSkills API not reachable: {err}. Is joshskills-api running on 127.0.0.1:10010?</div>}
        {failed.length > 0 && <div style={{ color: '#f87171', fontSize: '0.75rem' }}>{failed.length} failed JoshSkills job run(s) in the last 24 h: {failed.map(f => `${f.job} ${String(f.started_at).slice(0, 16)}`).join(', ')}</div>}
        <div style={{ ...MONO, fontSize: '0.75rem', color: '#cbd5e1' }}>{data ? statsLine(s) : (err ? '' : 'loading…')}</div>
        {s && s.closed > 0 && <div style={{ ...MONO, fontSize: '0.7rem', color: '#94a3b8', marginTop: 4 }}>
          exits: target {s.by_exit.target} · stop {s.by_exit.stop} · flat 15:55 {s.by_exit.flat_by + (s.by_exit.flat_lastbar || 0)} · open {s.open}
        </div>}
        <div style={{ fontSize: '0.7rem', color: '#fbbf24', marginTop: 6 }}>
          {data?.labels?.native}. {mode === 'replay' ? data?.labels?.replay + '.' : 'Live shadow: counts toward the 4-week, 20-trade gate (D2).'} Not evidence of edge.
        </div>
      </Panel>
      <div className="grid gap-3" style={{ gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)' }}>
        <Panel title="Equity curve (shadow account, $200,000 start)">
          {data?.equity?.length ? (
            <div style={{ height: 260 }}>
              <ResponsiveContainer>
                <LineChart data={data.equity} margin={{ top: 8, right: 12, left: 8, bottom: 0 }}>
                  <CartesianGrid stroke="#1e293b" />
                  <XAxis dataKey="date" stroke="#64748b" fontSize={10} />
                  <YAxis stroke="#64748b" fontSize={10} domain={['auto', 'auto']} width={70} />
                  <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #334155', fontSize: 11 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line type="monotone" dataKey="equity" name="native" stroke="#38bdf8" dot={{ r: 2 }} />
                  <Line type="monotone" dataKey="equity_alt" name="could have filled at" stroke="#fbbf24" strokeDasharray="4 3" dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>) : <div style={{ color: '#64748b', fontSize: '0.75rem' }}>no closed trades yet</div>}
        </Panel>
        <Panel title="By Time Machine verdict at entry">
          <table style={{ ...MONO, fontSize: '0.72rem', width: '100%' }}>
            <thead><tr><th style={th}>verdict</th><th style={th}>trades</th><th style={th}>won</th><th style={th}>avg R</th><th style={th}>P&L</th></tr></thead>
            <tbody>{Object.entries(s?.by_verdict || {}).map(([k, g]) => (
              <tr key={k}><td style={{ ...td, color: VERDICT_TONE[k] }}>{k}</td><td style={td}>{g.n}</td><td style={td}>{g.wins}</td><td style={td}>{rText(g.avg_r)}</td><td style={td}>{money(g.pnl)}</td></tr>))}</tbody>
          </table>
          <div style={{ fontSize: '0.68rem', color: '#64748b', marginTop: 6 }}>Every risk PASS is taken whatever the verdict, so this shows whether the Time Machine's warnings carried information. Small samples are noise.</div>
        </Panel>
      </div>
      <Panel title="Trade chart"><TradeChart trade={pick} /></Panel>
      <Panel title={`Trades (${trades.length})`}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ ...MONO, fontSize: '0.72rem', width: '100%' }}>
            <thead><tr>{['date', 'symbol', 'entry', 'qty', 'stop', 'target', 'exit', 'reason', 'R', 'P&L', 'could-have P&L', 'TM target/stop'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
            <tbody>{trades.map(t => (
              <tr key={t.id} onClick={() => setPick(t)} style={{ cursor: 'pointer', background: pick?.id === t.id ? '#1e293b' : 'transparent' }}>
                <td style={td}>{String(t.trade_date).slice(0, 10)}</td><td style={td}>{t.symbol}</td>
                <td style={td}>{Number(t.entry_price).toFixed(2)}</td><td style={td}>{Number(t.qty)}</td>
                <td style={td}>{Number(t.stop).toFixed(2)}</td><td style={td}>{Number(t.target).toFixed(2)}</td>
                <td style={td}>{t.exit_price != null ? Number(t.exit_price).toFixed(2) : 'open'}</td>
                <td style={{ ...td, color: EXIT_TONE[t.exit_reason] || '#94a3b8' }}>{t.exit_reason || '—'}</td>
                <td style={td}>{rText(t.r_multiple)}</td><td style={{ ...td, color: Number(t.pnl) >= 0 ? '#34d399' : '#f87171' }}>{money(t.pnl)}</td>
                <td style={td}>{money(t.alt_pnl)}</td>
                <td style={{ ...td, color: VERDICT_TONE[verdictOf(t.tm_verdict)] }}>{t.tm_p_target != null ? `${Math.round(t.tm_p_target * 100)}% / ${Math.round(t.tm_p_stop * 100)}%` : '—'}</td>
              </tr>))}</tbody>
          </table>
        </div>
      </Panel>
    </div>
  )
}

export default function JoshSkills() {
  const [tab, setTab] = useState('shadow')
  return (
    <div className="p-3 space-y-3">
      <div className="flex gap-2">
        {[['shadow', 'SHADOW TRADES'], ['catalog', 'SKILL CATALOGUE']].map(([k, label]) => (
          <button key={k} style={{ ...btn, ...(tab === k ? on : {}) }} onClick={() => setTab(k)}>{label}</button>))}
      </div>
      {tab === 'shadow' ? <ShadowView /> : <SkillLab />}
    </div>
  )
}
